import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Alert } from '@/ui/Alert/Alert'
import { Button } from '@/ui/Button/Button'
import { Chip } from '@/ui/Chip/Chip'
import { EmptyState } from '@/ui/EmptyState/EmptyState'
import { Icon } from '@/ui/Icon/Icon'
import { Skeleton } from '@/ui/Loading/Loading'
import { useSkeletonDelay } from '@/ui/Loading/useSkeletonDelay'
import { Inline } from '@/ui/layout/Inline'
import { Stack } from '@/ui/layout/Stack'
import { RowList, RowListItem } from '@/ui/RowList/RowList'
import { SectionHeader } from '@/ui/SectionHeader/SectionHeader'
import { resourcesFor } from '../api/resources'
import { ResourceRow } from '../components/console/ResourceRow'
import {
  isConnectionKind,
  reviewFlag,
} from '../components/console/resourceFacets'
import { type Crumb, WsPageShell } from '../components/WsPageShell'
import { ROUTES } from '../constants/routes'
import { resourcesCopy } from '../copy/resources'
import { useTopicsQuery } from '../hooks/useTopicsQuery'
import { useWorkspaceStore } from '../store/workspace'
import type { TopicResource } from '../types/api'

type Filter = 'all' | 'connections' | 'agent_created' | 'needs_review'

/**
 * The redesigned workspace resources index (§7): same grammar as the
 * topic tab — filter chips over kind/provenance facets, then
 * WORKSPACE-SCOPED and IN #TOPIC · n groups. Rows deep-link to `/r/:id`.
 * Each group ends with its add row, which opens the form in that group's
 * scope (D213: a list creates from its end).
 * Replaces the old workspace-page resource list under console-v2.
 */
export function WorkspaceResourcesPage() {
  const { workspaceId = '' } = useParams()
  const workspaces = useWorkspaceStore((s) => s.workspaces)
  const activeWorkspaceId = useWorkspaceStore((s) => s.activeWorkspaceId)
  const wsId = workspaceId || activeWorkspaceId || ''
  const workspaceName = workspaces.find((w) => w.workspace_id === wsId)?.name
  const [filter, setFilter] = useState<Filter>('all')

  const topicsQuery = useTopicsQuery(wsId)
  const topics = useMemo(() => topicsQuery.data ?? [], [topicsQuery.data])
  const wsQuery = useQuery({
    queryKey: ['resources', 'workspace', wsId],
    queryFn: () => resourcesFor({ kind: 'workspace', id: wsId }).list(),
    enabled: !!wsId,
    staleTime: 10_000,
  })
  // Per-topic pools (workspaces have a handful of topics; the query keys
  // are the ones every other surface already warms and caches).
  const topicIdList = useMemo(() => topics.map((t) => t.topic_id), [topics])
  const listsQuery = useQuery({
    queryKey: ['resources', 'topics-union', wsId],
    queryFn: async () => {
      const entries = await Promise.all(
        topicIdList.map(async (id) => {
          const res = await resourcesFor({ kind: 'topic', id }).list()
          return [id, res.resources] as const
        }),
      )
      return Object.fromEntries(entries) as Record<string, TopicResource[]>
    },
    enabled: topicIdList.length > 0,
    staleTime: 10_000,
  })

  const wsRes = wsQuery.data
  const topicLists = listsQuery.data
  const queries = [topicsQuery, wsQuery, listsQuery]
  const loading = queries.some((q) => q.isLoading)
  const failed = queries.some((q) => q.isError)
  const showSkeleton = useSkeletonDelay(loading)
  const retry = () => {
    for (const q of queries) void q.refetch()
  }

  // A topic's list includes the workspace-scoped resources shared into it, so
  // the union counts each resource once by id: the workspace's copy first.
  const all = useMemo(() => {
    const seen = new Set<string>()
    const unique: TopicResource[] = []
    for (const r of [
      ...(wsRes?.resources ?? []),
      ...Object.values(topicLists ?? {}).flat(),
    ]) {
      if (seen.has(r.id)) continue
      seen.add(r.id)
      unique.push(r)
    }
    return unique
  }, [wsRes, topicLists])
  const needsReviewCount = all.filter((r) => reviewFlag(r) !== null).length

  const matches = (r: TopicResource): boolean => {
    if (filter === 'all') return true
    if (filter === 'needs_review') return reviewFlag(r) !== null
    if (filter === 'connections') return isConnectionKind(r.kind)
    return r.author?.kind === 'agent'
  }

  const chips: [Filter, string, number][] = [
    ['all', resourcesCopy.facets.all, all.length],
    [
      'connections',
      resourcesCopy.facets.connections,
      all.filter((r) => isConnectionKind(r.kind)).length,
    ],
    [
      'agent_created',
      resourcesCopy.facets.agentCreated,
      all.filter((r) => r.author?.kind === 'agent').length,
    ],
    ['needs_review', resourcesCopy.facets.needsReview, needsReviewCount],
  ]

  const returnTo = { returnTo: ROUTES.workspaceResources(wsId) }
  const groups: {
    key: string
    label: string
    rows: TopicResource[]
    newHref: string
  }[] = [
    {
      key: 'workspace',
      label: resourcesCopy.list.workspaceScoped,
      rows: (wsRes?.resources ?? []).filter(matches),
      newHref: ROUTES.resourceNew(wsId),
    },
    ...topics.map((t) => ({
      key: t.topic_id,
      label: resourcesCopy.list.groupIn(t.name),
      rows: (topicLists?.[t.topic_id] ?? [])
        .filter((r) => !r.workspace_id)
        .filter(matches),
      newHref: ROUTES.topicResourceNew(t.topic_id),
    })),
  ]
  const addRow = (href: string) => (
    <Button variant="ghost" href={href} state={returnTo}>
      <Icon name="plus" size="icon-embedded" />
      {resourcesCopy.add}
    </Button>
  )

  const crumbs: Crumb[] = [
    {
      label: workspaceName ?? resourcesCopy.list.workspaceCrumb,
      to: ROUTES.home,
    },
    { label: resourcesCopy.list.crumb },
  ]

  return (
    <WsPageShell
      crumbs={crumbs}
      title={resourcesCopy.list.title}
      description={resourcesCopy.list.description}
    >
      <Stack gap="space-24">
        <Inline
          role="group"
          aria-label={resourcesCopy.filterGroup}
          gap="space-6"
        >
          {chips.map(([key, label, count]) => (
            <Chip
              key={key}
              variant="filter"
              size="default"
              label={label}
              count={loading ? undefined : count}
              pressed={filter === key}
              onPressedChange={() => setFilter(key)}
            />
          ))}
        </Inline>
        <div aria-busy={loading || undefined}>
          {loading ? (
            showSkeleton && <Skeleton lines={3} />
          ) : failed ? (
            <Alert tone="danger">
              <Inline gap="space-8" align="baseline">
                {resourcesCopy.loadFailed}
                <Button variant="outline" size="sm" onClick={retry}>
                  {resourcesCopy.retry}
                </Button>
              </Inline>
            </Alert>
          ) : all.length === 0 ? (
            <EmptyState
              title={resourcesCopy.list.workspaceEmpty}
              titleAs="h2"
              action={wsId ? addRow(ROUTES.resourceNew(wsId)) : undefined}
            />
          ) : groups.every((g) => g.rows.length === 0) ? (
            <EmptyState
              variant="inline"
              description={resourcesCopy.list.filteredEmpty(filter)}
              action={
                <Button variant="outline" onClick={() => setFilter('all')}>
                  {resourcesCopy.clearFilters}
                </Button>
              }
            />
          ) : (
            <Stack gap="space-24">
              {groups.map((g) => (
                <section key={g.key}>
                  <SectionHeader
                    rule
                    trailingSpacing="section"
                    count={g.rows.length}
                  >
                    {g.label}
                  </SectionHeader>
                  <RowList variant="plain">
                    {g.rows.map((r) => (
                      <ResourceRow key={r.id} resource={r} />
                    ))}
                    <RowListItem>{addRow(g.newHref)}</RowListItem>
                  </RowList>
                </section>
              ))}
            </Stack>
          )}
        </div>
      </Stack>
    </WsPageShell>
  )
}
